import { useState, useEffect, useRef, useCallback, Fragment } from 'react';
import { Upload, X, Film, Crown, Trash2, AlertCircle } from 'lucide-react';
import { Loader } from './ui/Loader';
import { motion, AnimatePresence } from 'motion/react';
import { projectId, API_BASE } from '../utils/supabase/info';
import { containsProfanity } from '../utils/profanity-filter';
import { VideoUploader } from './VideoUploader';
import { VideoPlayer } from './VideoPlayer';
import { ConfirmDialog } from './ConfirmDialog';
import { handleApiError } from '../utils/api-error-handler';
import { devLog, devError } from '../utils/logger';
import { canUploadVideo } from '../utils/accessPolicy';
import { toast } from '../utils/toast';
import { useModalFocus } from '../utils/hooks/useModalFocus';
import { triggerAndroidHaptic } from '../utils/haptics';
import { compressImageForUpload, PORTFOLIO_PRESET } from '../utils/imageCompression';
import { useImageCropModal } from '../utils/hooks/useImageCropModal';
import { PROJECT_COVER_SPEC_4_3 } from '../utils/media/imageCrop';
import { PostDetailView } from './PostDetailView';
import { FullscreenShell } from './layout/FullscreenShell';


interface EditPostPageProps {
  post: any;
  accessToken: string | null;
  onPostUpdated: () => void;
  onCancel: () => void;
  onNavigateToSubscription?: () => void;
}

export function EditPostPage({ post, accessToken, onPostUpdated, onCancel, onNavigateToSubscription }: EditPostPageProps) {
  useModalFocus(true, { fullscreen: true }); // Full-screen: hide header/footer while open
  const { openCrop, cropModal } = useImageCropModal();
  // Editor contract:
  // - cover image is separate from gallery images (max 3)
  // - cancel does not auto-save; navigation-away may auto-save in later phase
  const [title, setTitle] = useState(post.title || '');
  const [description, setDescription] = useState(post.description || '');
  const [imageFiles, setImageFiles] = useState<File[]>([]);
  const [imagePreviews, setImagePreviews] = useState<string[]>(post.imageUrls || []); // ✅ FIXED: Changed from post.images to post.imageUrls
  const [coverImageFile, setCoverImageFile] = useState<File | null>(null);
  const [coverImagePreview, setCoverImagePreview] = useState<string | null>(post.coverImageUrl || null);
  const [videoId, setVideoId] = useState<string | null>(post.videoId || null);
  const [originalVideoId] = useState<string | null>(post.videoId || null); // ✅ NEW: Track original video to detect new uploads
  const [showVideoUploader, setShowVideoUploader] = useState(false);
  const [deletingVideo, setDeletingVideo] = useState(false);
  const [userProfile, setUserProfile] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showDeleteVideoConfirm, setShowDeleteVideoConfirm] = useState(false);
  const [videoEncodingStatus, setVideoEncodingStatus] = useState<'unknown' | 'encoding' | 'finished' | 'failed'>('unknown');
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [showPublishConfirm, setShowPublishConfirm] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [loadingAction, setLoadingAction] = useState<'draft' | 'publish' | 'save' | null>(null);
  const [compressingImages, setCompressingImages] = useState(false); // ✅ NEW: Track image compression progress
  const [mediaProcessingMessage, setMediaProcessingMessage] = useState('Behandler bilder...');
  const [isSavingDraft, setIsSavingDraft] = useState(false);
  const [isDraft, setIsDraft] = useState(post.isDraft || false); // ✅ NEW: Track draft status
  const originalImageUrlsRef = useRef<string[]>((post.imageUrls || []) as string[]);
  const originalCoverImageUrlRef = useRef<string | null>((post.coverImageUrl || null) as string | null);
  const initialSnapshotRef = useRef({
    title: post.title || '',
    description: post.description || '',
    imageCount: (post.imageUrls || []).length,
    hasCover: !!post.coverImageUrl,
    videoId: (post.videoId || null) as string | null,
  });
  const skipAutoSaveRef = useRef(false);
  const autoSaveInFlightRef = useRef(false);
  const hasUnsavedChangesRef = useRef(false);
  const accessTokenRef = useRef<string | null>(accessToken);
  const draftStateRef = useRef({
    isDraft: post.isDraft || false,
    title: post.title || '',
    description: post.description || '',
    videoId: (post.videoId || null) as string | null,
    coverImageFile: null as File | null,
    coverImagePreview: (post.coverImageUrl || null) as string | null,
    imagePreviews: (post.imageUrls || []) as string[],
    imageFiles: [] as File[],
    postId: post.id as string,
  });

  // Standardized fullscreen shell uses a fixed header on mobile for all platforms.

  // ✅ NEW: Media loading state tracker (like PostDetailView)
  const [mediaLoadingStates, setMediaLoadingStates] = useState({
    coverImage: !post.coverImageUrl, // true if no cover (already "loaded")
    images: (post.imageUrls || []).map(() => false) // false for each existing image
  });

  // ✅ Check if all media is ready
  const allMediaReady = mediaLoadingStates.coverImage && 
                         mediaLoadingStates.images.every(ready => ready);
  const hasUnsavedChanges =
    title.trim() !== initialSnapshotRef.current.title ||
    description.trim() !== initialSnapshotRef.current.description ||
    imagePreviews.length !== initialSnapshotRef.current.imageCount ||
    !!coverImagePreview !== initialSnapshotRef.current.hasCover ||
    (videoId || null) !== initialSnapshotRef.current.videoId;
  hasUnsavedChangesRef.current = hasUnsavedChanges;
  accessTokenRef.current = accessToken;
  draftStateRef.current = {
    isDraft,
    title,
    description,
    videoId,
    coverImageFile,
    coverImagePreview,
    imagePreviews,
    imageFiles,
    postId: post.id,
  };

  const applyMediaPayload = (
    payload: any,
    snapshot: {
      imagePreviews: string[];
      imageFiles: File[];
      coverImageFile: File | null;
      coverImagePreview: string | null;
    }
  ) => {
    const originalImageUrls = originalImageUrlsRef.current;
    const currentExistingImageUrls = snapshot.imagePreviews.filter(
      (preview) => !preview.startsWith('data:image')
    );

    const hasRemovedExistingImages =
      currentExistingImageUrls.length < originalImageUrls.length ||
      originalImageUrls.some((url) => !currentExistingImageUrls.includes(url));

    if (originalImageUrls.length > 0 && currentExistingImageUrls.length === 0) {
      payload.clearImages = true;
    } else if (hasRemovedExistingImages) {
      payload.keepImageUrls = currentExistingImageUrls;
    }

    if (snapshot.imageFiles.length > 0) {
      const newImagesBase64 = snapshot.imagePreviews.filter((preview) =>
        preview.startsWith('data:image')
      );
      if (newImagesBase64.length > 0) {
        payload.imageBase64 = newImagesBase64.join(',');
      }
    }

    if (snapshot.coverImageFile && snapshot.coverImagePreview) {
      payload.coverImageBase64 = snapshot.coverImagePreview;
    } else if (!snapshot.coverImagePreview && !!originalCoverImageUrlRef.current) {
      payload.clearCoverImage = true;
    }
  };

  const saveDraftSilently = useCallback(async (
    reason: string,
    options?: { keepalive?: boolean; showToast?: boolean }
  ): Promise<boolean> => {
    const accessTokenValue = accessTokenRef.current;
    const snapshot = draftStateRef.current;
    if (!accessTokenValue) return false;
    if (!snapshot.isDraft) return false;
    if (autoSaveInFlightRef.current) return false;
    if (!hasUnsavedChangesRef.current) return false;

    autoSaveInFlightRef.current = true;
    setIsSavingDraft(true);
    const loadingToastId = options?.showToast ? toast.loading('Lagrer utkast...') : null;

    try {
      const payload: any = {
        title: snapshot.title.trim(),
        description: snapshot.description.trim(),
        categories: [],
        videoId: snapshot.videoId,
      };
      applyMediaPayload(payload, {
        imagePreviews: snapshot.imagePreviews,
        imageFiles: snapshot.imageFiles,
        coverImageFile: snapshot.coverImageFile,
        coverImagePreview: snapshot.coverImagePreview,
      });

      const response = await fetch(`${API_BASE}/posts/${snapshot.postId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessTokenValue}`,
        },
        body: JSON.stringify(payload),
        keepalive: options?.keepalive === true,
      });

      if (!response.ok) {
        devError('Auto-save draft failed in EditPost', { reason, status: response.status, postId: snapshot.postId });
        return false;
      }
      if (options?.showToast) {
        toast.dismiss(loadingToastId!);
        toast.success('Utkast lagret');
      }
      return true;
    } catch (error) {
      devError('Auto-save draft error in EditPost', { reason, postId: post.id, error });
      if (options?.showToast) {
        toast.dismiss(loadingToastId!);
        toast.error('Kunne ikke lagre utkast');
      }
      return false;
    } finally {
      if (loadingToastId !== null) {
        toast.dismiss(loadingToastId);
      }
      setIsSavingDraft(false);
      autoSaveInFlightRef.current = false;
    }
  }, []);

  const navigateToSubscriptionWithAutoSave = async () => {
    if (hasUnsavedChangesRef.current) {
      await saveDraftSilently('subscription-link', { showToast: true });
    }
    skipAutoSaveRef.current = true;
    onCancel();
    window.setTimeout(() => {
      if (onNavigateToSubscription) {
        onNavigateToSubscription();
      } else {
        window.location.hash = 'subscription';
      }
    }, 0);
  };

  // ✅ Callbacks from media components
  const handleCoverImageLoad = () => {
    setMediaLoadingStates(prev => ({ ...prev, coverImage: true }));
  };

  const handleImageLoad = (index: number) => {
    setMediaLoadingStates(prev => ({
      ...prev,
      images: prev.images.map((ready, i) => i === index ? true : ready)
    }));
  };

  // ✅ FIX: Fetch fresh post data whenever post.id changes (when reopening editor)
  useEffect(() => {
    if (post) {
      // Update state with fresh post data
      setTitle(post.title || '');
      setDescription(post.description || '');
      setImagePreviews(post.imageUrls || []); // ✅ FIXED: Changed from post.images to post.imageUrls
      setCoverImagePreview(post.coverImageUrl || null);
      setVideoId(post.videoId || null);
      setIsDraft(post.isDraft || false); // ✅ NEW: Update draft status
      // Clear file inputs when switching posts
      setImageFiles([]);
      setCoverImageFile(null);
      setError('');
      initialSnapshotRef.current = {
        title: post.title || '',
        description: post.description || '',
        imageCount: (post.imageUrls || []).length,
        hasCover: !!post.coverImageUrl,
        videoId: post.videoId || null,
      };
      originalImageUrlsRef.current = (post.imageUrls || []) as string[];
      originalCoverImageUrlRef.current = (post.coverImageUrl || null) as string | null;
    }
  }, [post.id]); // Re-run when post.id changes

  // Refetch profile when accessToken changes (e.g. after re-login)
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    if (accessToken) fetchUserProfile();
  }, [accessToken]);

  const fetchUserProfile = async () => {
    try {
      const response = await fetch(
        `${API_BASE}/profile/me`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        setUserProfile(data.profile);
      }
    } catch (error) {
      devError('Error fetching user profile:', error);
    }
  };

  const handleDeleteVideo = () => {
    if (!videoId) return;
    setShowDeleteVideoConfirm(true);
  };

  const confirmDeleteVideo = async () => {
    setDeletingVideo(true);
    setError('');
    setShowDeleteVideoConfirm(false);

    try {
      const response = await fetch(
        `${API_BASE}/videos/${videoId}`,
        {
          method: 'DELETE',
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error('Kunne ikke slette video');
      }

      setVideoId(null);
      setShowVideoUploader(false);
      setVideoEncodingStatus('unknown');
    } catch (err: any) {
      setError(err.message || 'Kunne ikke slette video');
    } finally {
      setDeletingVideo(false);
    }
  };

  // When we have a videoId, check encoding status so we can show "Video konverterer" if still encoding
  useEffect(() => {
    if (!videoId || !accessToken) {
      setVideoEncodingStatus('unknown');
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/videos/${videoId}/status`, {
          headers: { Authorization: `Bearer ${accessToken}` },
        });
        if (cancelled) return;
        if (!res.ok) {
          const errorData = await res.json().catch(() => ({}));
          if (res.status === 422 && errorData?.errorCode === 'VIDEO_TOO_LONG') {
            setVideoEncodingStatus('failed');
            setError(errorData.error || 'Videoen er for lang. Maksimal lengde er 5 minutter.');
            return;
          }
          setVideoEncodingStatus('finished');
          return;
        }
        const data = await res.json();
        const s = data.status;
        if (s === 0 || s === 1 || s === 2) setVideoEncodingStatus('encoding');
        else if (s === 3 || s === 4) setVideoEncodingStatus('finished');
        else if (s === 5) setVideoEncodingStatus('failed');
        else setVideoEncodingStatus('finished');
      } catch {
        if (!cancelled) setVideoEncodingStatus('finished');
      }
    })();
    return () => { cancelled = true; };
  }, [videoId, accessToken]);

  const handleVideoUploaded = (newVideoId: string) => {
    setVideoId(newVideoId);
    setShowVideoUploader(false);
  };

  const handleImageChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;
    
    const maxImages = 3;
    const remainingSlots = maxImages - imagePreviews.length;

    if (remainingSlots <= 0) {
      toast.error('Du kan maksimalt ha 3 bilder per prosjekt');
      return;
    }

    // Validate file sizes
    const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
    const filesToAdd = Array.from(files).slice(0, remainingSlots);
    
    for (const file of filesToAdd) {
      if (file.size > MAX_FILE_SIZE) {
        toast.error(`Bilde "${file.name}" er for stort. Maksimal størrelse er 10MB.`);
        return;
      }
      
      if (!file.type.startsWith('image/')) {
        toast.error(`"${file.name}" er ikke et gyldig bilde. Kun PNG, JPG, JPEG er tillatt.`);
        return;
      }
    }
    
    // Process all files in parallel and batch state update
    try {
      setMediaProcessingMessage('Laster opp bilder...');
      setCompressingImages(true);
      const compressedPreviews = await Promise.all(
        filesToAdd.map(file => compressImageForUpload(file, PORTFOLIO_PRESET))
      );
      
      // Single state update for all images - prevents multiple re-renders
      setImageFiles(prev => [...prev, ...filesToAdd]);
      setImagePreviews(prev => [...prev, ...compressedPreviews]);
      setError('');
    } catch (error) {
      devError('Error processing images:', error);
      toast.error('Kunne ikke behandle bildene. Prøv igjen.');
    } finally {
      setCompressingImages(false);
      setMediaProcessingMessage('Behandler bilder...');
    }
  };

  const removeImage = (index: number) => {
    // ✅ FIX: Calculate how many existing images there are (loaded from backend)
    // imagePreviews contains BOTH existing URLs and new base64 images
    // imageFiles contains ONLY newly uploaded File objects
    const existingImagesCount = imagePreviews.length - imageFiles.length;
    
    // If removing an existing image (from backend), only remove from previews
    if (index < existingImagesCount) {
      setImagePreviews(imagePreviews.filter((_, i) => i !== index));
    } 
    // If removing a newly uploaded image, remove from BOTH imageFiles and imagePreviews
    else {
      const newImageIndex = index - existingImagesCount;
      setImageFiles(imageFiles.filter((_, i) => i !== newImageIndex));
      setImagePreviews(imagePreviews.filter((_, i) => i !== index));
    }
  };

  const handleCoverImageChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
    
    if (file.size > MAX_FILE_SIZE) {
      toast.error('Cover-bildet er for stort. Maksimal størrelse er 10MB.');
      return;
    }
    
    if (!file.type.startsWith('image/')) {
      toast.error('Cover-bilde må være et gyldig bilde (PNG, JPG, JPEG).');
      return;
    }

    try {
      setMediaProcessingMessage('Laster opp cover-bilde...');
      setCompressingImages(true);
      const croppedFile = await openCrop(file, PROJECT_COVER_SPEC_4_3);
      const compressedPreview = await compressImageForUpload(croppedFile, PORTFOLIO_PRESET);
      setCoverImageFile(croppedFile);
      setCoverImagePreview(compressedPreview);
      setError('');
    } catch (error) {
      if (error instanceof Error && error.message.includes('avbrutt')) return;
      devError('Error processing cover image:', error);
      toast.error('Kunne ikke behandle cover-bildet. Prøv igjen.');
    } finally {
      setCompressingImages(false);
      setMediaProcessingMessage('Behandler bilder...');
    }
  };

  const removeCoverImage = () => {
    setCoverImageFile(null);
    setCoverImagePreview(null);
  };

  // ✅ NEW: Handle submit with draft publishing support
  const handleSubmit = async (publishDraft = false) => {
    // If publishing a draft, require full validation
    // If updating a draft (not publishing), skip validation
    const isPublishing = isDraft && publishDraft;
    const isUpdatingDraft = isDraft && !publishDraft;

    // Only validate if publishing a draft or editing a published post
    if (!isUpdatingDraft) {
      if (!coverImagePreview) {
        toast.error('Cover-bilde er påkrevd for å publisere');
        return;
      }
      if (!title.trim() || !description.trim()) {
        toast.error('Tittel og beskrivelse er påkrevd');
        return;
      }

      if (containsProfanity(title) || containsProfanity(description)) {
        toast.error('Innholdet inneholder upassende språk. Vennligst bruk et profesjonelt språk.');
        return;
      }

    }

    setLoadingAction(isPublishing ? 'publish' : isUpdatingDraft ? 'draft' : 'save');
    setLoading(true);
    setError('');

    try {
      const payload: any = {
        title: title.trim(),
        description: description.trim(),
        categories: [],
        videoId,
      };

      // ✅ NEW: If publishing a draft, set isDraft to false
      if (isPublishing) {
        payload.isDraft = false;
      }

      applyMediaPayload(payload, {
        imagePreviews,
        imageFiles,
        coverImageFile,
        coverImagePreview,
      });

      const response = await fetch(
        `${API_BASE}/posts/${post.id}`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify(payload),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        handleApiError(errorData, () => {
          void navigateToSubscriptionWithAutoSave();
        });
        setLoading(false);
        setLoadingAction(null);
        return;
      }

      // ✅ FIX: Update local state with backend response to reflect saved changes immediately
      const data = await response.json();
      if (data.post) {
        // Update all state with fresh data from backend
        setTitle(data.post.title || '');
        setDescription(data.post.description || '');
        setImagePreviews(data.post.imageUrls || []); // ✅ FIXED: Changed from post.images to post.imageUrls
        setCoverImagePreview(data.post.coverImageUrl || null);
        setVideoId(data.post.videoId || null);
        setIsDraft(data.post.isDraft || false); // ✅ NEW: Update draft status
        // Clear file inputs since images are now saved
        setImageFiles([]);
        setCoverImageFile(null);
        originalImageUrlsRef.current = (data.post.imageUrls || []) as string[];
        originalCoverImageUrlRef.current = (data.post.coverImageUrl || null) as string | null;
        initialSnapshotRef.current = {
          title: data.post.title || '',
          description: data.post.description || '',
          imageCount: (data.post.imageUrls || []).length,
          hasCover: !!data.post.coverImageUrl,
          videoId: data.post.videoId || null,
        };
      }

      skipAutoSaveRef.current = true;
      if (isPublishing) toast.success('Prosjekt publisert!');
      onPostUpdated();
    } catch (err: any) {
      setError(err.message || 'Noe gikk galt. Prøv igjen senere.');
    } finally {
      setLoading(false);
      setLoadingAction(null);
    }
  };

  /** Validering + vis bekreftelsesdialog før publish av utkast. */
  const requestPublish = () => {
    if (!coverImagePreview) {
      toast.error('Cover-bilde er påkrevd for å publisere');
      return;
    }
    if (!title.trim() || !description.trim()) {
      toast.error('Tittel og beskrivelse er påkrevd');
      return;
    }
    if (containsProfanity(title) || containsProfanity(description)) {
      toast.error('Innholdet inneholder upassende språk. Vennligst bruk et profesjonelt språk.');
      return;
    }
    setShowPublishConfirm(true);
  };

  // ✅ NEW: Cleanup handler - Deletes NEW video if user cancels without saving
  const handleCancel = async () => {
    skipAutoSaveRef.current = true;
    devLog('EditPost cancel requested', { hasUnsavedChanges, postId: post.id });
    // Check if user added a NEW video (not the original one)
    const hasNewVideo = videoId && videoId !== originalVideoId;
    
    if (hasNewVideo) {
      try {
        devError('🧹 Cleaning up new unsaved video:', videoId);
        
        const response = await fetch(
          `${API_BASE}/videos/${videoId}`,
          {
            method: 'DELETE',
            headers: {
              Authorization: `Bearer ${accessToken}`,
            },
          }
        );

        if (response.ok) {
          devError('✅ New video cleaned up successfully');
        } else {
          devError('⚠️ Failed to cleanup new video - will be orphaned');
        }
      } catch (err) {
        devError('⚠️ Error during video cleanup:', err);
        // Don't block cancel - user wants to exit
      }
    }
    
    // Close modal
    onCancel();
  };

  const requestCancel = () => {
    if (loading || deletingVideo || compressingImages || isSavingDraft) return;
    if (hasUnsavedChanges) {
      setShowCancelConfirm(true);
      return;
    }
    void handleCancel();
  };

  useEffect(() => {
    const handlePageHide = () => {
      if (skipAutoSaveRef.current) return;
      if (!hasUnsavedChangesRef.current) return;
      void saveDraftSilently('pagehide', { keepalive: true });
    };
    const handleBeforeUnload = () => {
      if (skipAutoSaveRef.current) return;
      if (!hasUnsavedChangesRef.current) return;
      void saveDraftSilently('beforeunload', { keepalive: true });
    };
    window.addEventListener('pagehide', handlePageHide);
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('pagehide', handlePageHide);
      window.removeEventListener('beforeunload', handleBeforeUnload);
      if (!skipAutoSaveRef.current && hasUnsavedChangesRef.current) {
        void saveDraftSilently('component-unmount', { keepalive: true });
      }
    };
  }, []);

  const editPostContent = (
    <>
          {error && (
            <div className="mb-6 p-4 bg-red-50 border-2 border-red-200 rounded-2xl">
              <p className="text-red-700">{error}</p>
            </div>
          )}

          <div className="space-y-6">
            {/* Cover Image – øverst */}
            <div>
              <label className="block mb-2 text-gray-700 font-medium">
                Cover-bilde
              </label>
              <p className="text-sm text-gray-500 mb-3">
                Dette bildet vises på prosjektkortet.
              </p>
              {coverImagePreview ? (
                <div className="relative">
                  <img
                    src={coverImagePreview}
                    alt="Cover preview"
                    className="w-full aspect-[4/3] object-cover rounded-2xl border-2 border-purple-300"
                    onLoad={handleCoverImageLoad}
                  />
                  <button
                    type="button"
                    onClick={removeCoverImage}
                    className="absolute top-3 right-3 p-2 bg-red-600 text-white rounded-full hover:bg-red-700 shadow-lg"
                  >
                    <X className="w-5 h-5" />
                  </button>
                  <div className="absolute bottom-3 left-3 px-3 py-1.5 bg-purple-600 bg-opacity-90 text-white rounded-full text-sm font-medium">
                    Cover-bilde
                  </div>
                  {/* Change cover image button */}
                  <label
                    htmlFor="cover-image-change"
                    className="absolute bottom-3 right-3 px-4 py-2 bg-purple-600 bg-opacity-90 text-white rounded-full cursor-pointer hover:bg-opacity-100 transition-all text-sm font-medium"
                  >
                    Endre
                    <input
                      id="cover-image-change"
                      type="file"
                      accept="image/*"
                      onChange={handleCoverImageChange}
                      className="hidden"
                    />
                  </label>
                </div>
              ) : (
                <label
                  htmlFor="cover-image-upload"
                  className="flex flex-col items-center justify-center w-full aspect-[4/3] border-2 border-dashed border-purple-400 rounded-2xl cursor-pointer hover:border-purple-600 hover:bg-purple-50 transition-all"
                >
                  <Upload className="w-12 h-12 text-purple-500 mb-2" />
                  <p className="text-purple-700 font-medium">Klikk for å laste opp cover-bilde</p>
                  <p className="text-purple-400 text-sm">PNG, JPG eller JPEG</p>
                  <input
                    id="cover-image-upload"
                    type="file"
                    accept="image/*"
                    onChange={handleCoverImageChange}
                    className="hidden"
                  />
                </label>
              )}
            </div>

            {/* Title */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-gray-700">
                  Prosjekttittel *
                </label>
                <span className={`text-sm ${title.length > 100 ? 'text-red-600 font-medium' : 'text-gray-500'}`}>
                  {title.length}/100
                </span>
              </div>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="F.eks. 'Moderniserte et kjøkken'"
                className="w-full border-2 border-gray-300 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                maxLength={100}
              />
            </div>

            {/* Description */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-gray-700">
                  Beskrivelse *
                </label>
                <span className={`text-sm ${description.length > 1500 ? 'text-red-600 font-medium' : 'text-gray-500'}`}>
                  {description.length}/1500
                </span>
              </div>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Beskriv prosjektet, hva du gjorde, hvilke utfordringer du løste..."
                rows={6}
                className="w-full border-2 border-gray-300 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none"
                maxLength={1500}
              />
            </div>

            <div className="p-3 bg-blue-50 border border-blue-200 rounded-xl text-sm text-blue-900">
              Husk å oppdatere profilen din med eventuelle ferdigheter brukt i dette prosjektet.
            </div>

            {/* Additional Images (up to 3 total) */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-gray-700 font-medium">Bilder av prosjektet</label>
                <span className="text-gray-500 text-sm">Opptil 3 bilder</span>
              </div>
              <p className="text-sm text-gray-500 mb-3">
                Vis prosjektet fra forskjellige vinkler eller i ulike stadier.
              </p>
              {imagePreviews.length > 0 ? (
                <div className="space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {imagePreviews.map((preview, index) => (
                      <div key={index} className="relative">
                        <img
                          src={preview}
                          alt={`Preview ${index + 1}`}
                          className="w-full max-h-[28rem] object-contain rounded-2xl border-2 border-purple-200 bg-gray-50"
                          onLoad={() => handleImageLoad(index)}
                        />
                        <button
                          type="button"
                          onClick={() => removeImage(index)}
                          className="absolute top-3 right-3 p-2 bg-red-600 text-white rounded-full hover:bg-red-700 shadow-lg"
                        >
                          <X className="w-5 h-5" />
                        </button>
                        <div className="absolute bottom-3 left-3 px-3 py-1 bg-black bg-opacity-60 text-white rounded-full text-sm">
                          Bilde {index + 1}
                        </div>
                      </div>
                    ))}
                  </div>
                  {/* Add more images button */}
                  {imagePreviews.length < 3 && (
                    <label
                      htmlFor="image-upload-more"
                      className="flex items-center justify-center gap-2 w-full py-3 border-2 border-dashed border-purple-300 rounded-xl cursor-pointer hover:border-purple-500 hover:bg-purple-50 transition-all"
                    >
                      <Upload className="w-5 h-5 text-purple-400" />
                      <span className="text-purple-700">Legg til flere bilder</span>
                      <input
                        id="image-upload-more"
                        type="file"
                        accept="image/*"
                        multiple
                        onChange={handleImageChange}
                        className="hidden"
                      />
                    </label>
                  )}
                </div>
              ) : (
                <label
                  htmlFor="image-upload"
                  className="flex flex-col items-center justify-center w-full h-64 border-2 border-dashed border-purple-400 rounded-2xl cursor-pointer hover:border-purple-600 hover:bg-purple-50 transition-all"
                >
                  <Upload className="w-12 h-12 text-purple-500 mb-2" />
                  <p className="text-purple-700 font-medium">Klikk for å laste opp bilder</p>
                  <p className="text-purple-400 text-sm">PNG, JPG eller JPEG (opptil 3 bilder)</p>
                  <input
                    id="image-upload"
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={handleImageChange}
                    className="hidden"
                  />
                </label>
              )}
            </div>

            {/* Video Section - Show for all users, but with restrictions for free users */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-gray-700">Prosjektvideo</label>
                {canUploadVideo(userProfile) && (
                  <div className="flex items-center gap-2 px-3 py-1 bg-gradient-to-r from-yellow-400 to-orange-400 rounded-full">
                    <Crown className="w-4 h-4 text-white" />
                    <span className="text-white text-sm">Pluss-funksjon</span>
                  </div>
                )}
              </div>

              {videoId ? (
                <div>
                  {videoEncodingStatus === 'encoding' ? (
                    <div className="p-4 bg-amber-50 border-2 border-amber-200 rounded-xl flex flex-col gap-3">
                      <p className="text-sm text-amber-900">
                        Video konverterer. Slett og prøv på nytt, eller vent til den er ferdig.
                      </p>
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={handleDeleteVideo}
                          disabled={deletingVideo || !canUploadVideo(userProfile)}
                          className="inline-flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium"
                        >
                          {deletingVideo ? <Loader size="sm" className="text-white" /> : <Trash2 className="w-4 h-4" />}
                          Slett video
                        </button>
                      </div>
                    </div>
                  ) : videoEncodingStatus === 'failed' ? (
                    <div className="p-4 bg-red-50 border-2 border-red-200 rounded-xl flex flex-col gap-3">
                      <p className="text-sm text-red-800">
                        Encoding feilet. Du kan slette og prøve på nytt.
                      </p>
                      <button
                        type="button"
                        onClick={handleDeleteVideo}
                        disabled={deletingVideo || !canUploadVideo(userProfile)}
                        className="inline-flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium"
                      >
                        {deletingVideo ? <Loader size="sm" className="text-white" /> : <Trash2 className="w-4 h-4" />}
                        Slett video
                      </button>
                    </div>
                  ) : (
                  <div className="relative">
                    <VideoPlayer videoId={videoId} accessToken={accessToken} className="w-full h-64" />
                    <button
                      type="button"
                      onClick={handleDeleteVideo}
                      disabled={deletingVideo || !canUploadVideo(userProfile)}
                      className="absolute top-3 right-3 p-2 bg-red-600 text-white rounded-full hover:bg-red-700 shadow-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      title={!canUploadVideo(userProfile) ? 'Krever Pluss-abonnement' : 'Slett video'}
                    >
                      <Trash2 className="w-5 h-5" />
                    </button>
                  </div>
                  )}
                  {!canUploadVideo(userProfile) && (
                    <div className="mt-3 p-3 bg-yellow-50 border-2 border-yellow-200 rounded-xl flex items-start gap-3">
                      <AlertCircle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                      <div className="flex-1">
                        <p className="text-sm text-yellow-800">
                          Du har en video fra Pluss-abonnementet. Oppgrader for å endre eller slette videoen.
                        </p>
                        <button
                          type="button"
                          onClick={() => void navigateToSubscriptionWithAutoSave()}
                          className="mt-2 text-sm text-yellow-700 underline hover:text-yellow-900"
                        >
                          Se Pluss-planer
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div>
                  <button
                    type="button"
                    onClick={() => canUploadVideo(userProfile) ? setShowVideoUploader(true) : void navigateToSubscriptionWithAutoSave()}
                    disabled={isSavingDraft}
                    className={`w-full flex items-center justify-center gap-2 py-4 border-2 border-dashed rounded-xl transition-all ${
                      canUploadVideo(userProfile)
                        ? 'border-purple-300 hover:border-purple-500 hover:bg-purple-50 cursor-pointer'
                        : 'border-gray-300 bg-gray-50 cursor-not-allowed opacity-60'
                    }`}
                  >
                    <Film className="w-6 h-6 text-purple-600" />
                    <span className="text-purple-700 font-medium">Legg til video</span>
                    {!canUploadVideo(userProfile) && (
                      <Crown className="w-5 h-5 text-yellow-500" />
                    )}
                  </button>
                  {!canUploadVideo(userProfile) && (
                    <p className="mt-2 text-sm text-gray-600 flex items-center gap-2">
                      <Crown className="w-4 h-4 text-yellow-500" />
                      Videoopplasting krever Talentfunn Pluss.
                      <button
                        type="button"
                        onClick={() => void navigateToSubscriptionWithAutoSave()}
                        className="text-purple-600 underline hover:text-purple-800"
                      >
                        Oppgrader
                      </button>
                    </p>
                  )}
                </div>
              )}
            </div>

            {/* Action buttons */}
            <div className="flex flex-wrap gap-4 pt-4 xl:pb-10">
              <button
                onClick={requestCancel}
                className="flex-1 min-w-[100px] px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 transition-colors"
              >
                Avbryt
              </button>

              <button
                type="button"
                onClick={() => {
                  triggerAndroidHaptic('light');
                  const hasContent = title.trim() || description.trim() || coverImagePreview || imagePreviews.length > 0;
                  if (!hasContent) {
                    toast.info('Legg inn tittel, beskrivelse eller bilder for å forhåndsvise.');
                    return;
                  }
                  setShowPreview(true);
                }}
                className="px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 transition-all"
              >
                Forhåndsvis
              </button>
              
              {/* ✅ NEW: Conditional buttons based on draft status */}
              {isDraft ? (
                <>
                  <button
                    type="button"
                    onClick={() => {
                      triggerAndroidHaptic('light');
                      handleSubmit(false);
                    }}
                    disabled={loading}
                    className="flex-1 min-w-[140px] px-6 py-3 border-2 border-purple-300 text-purple-700 rounded-xl hover:bg-purple-50 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Oppdater utkast
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      triggerAndroidHaptic('light');
                      requestPublish();
                    }}
                    disabled={loading || !coverImagePreview || !title.trim() || !description.trim()}
                    className="flex-1 min-w-[100px] px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-xl hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Publiser
                  </button>
                </>
              ) : (
                <button
                  type="button"
                  onClick={() => {
                    triggerAndroidHaptic('light');
                    handleSubmit(false);
                  }}
                  disabled={loading || !coverImagePreview || !title.trim() || !description.trim()}
                  className="flex-1 min-w-[140px] px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-xl hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Lagre endringer
                </button>
              )}
            </div>
          </div>
    </>
  );

  const loadingLabel = loadingAction === 'draft' ? 'Lagrer utkast...' : loadingAction === 'publish' ? 'Publiserer...' : 'Lagrer endringer...';

  return (
    <AnimatePresence>
      {/* Fullscreen loading overlay – samme type som CreatePostPage / Lagre utkast */}
      <AnimatePresence key="edit-post-loading">
        {loading && (
          <motion.div
            key="edit-post-loading-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 talentfunn-modal-overlay backdrop-blur-md flex items-center justify-center z-[100]"
          >
            <div className="bg-white rounded-3xl p-8 shadow-2xl max-w-md w-full mx-4">
              <div className="flex flex-col items-center gap-6">
                {/* Animated spinner - aria-hidden so only loading label is announced */}
                <div className="relative" aria-hidden="true">
                  <Loader size={60} className="text-purple-600" aria-hidden="true" />
                </div>
                <div className="text-center">
                  <h3 className="text-xl text-gray-900 mb-2">{loadingLabel}</h3>
                  <p className="text-gray-600 text-sm">Dette kan ta noen sekunder</p>
                </div>
                <div className="w-full bg-purple-100 rounded-full h-2 overflow-hidden" aria-hidden="true">
                  <motion.div
                    className="h-full bg-gradient-to-r from-purple-600 to-pink-600"
                    initial={{ width: '0%' }}
                    animate={{ width: '100%' }}
                    transition={{ duration: 3, ease: 'easeInOut' }}
                  />
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <FullscreenShell
        key="edit-post-shell"
        title="Rediger prosjekt"
        onClose={requestCancel}
        onBack={requestCancel}
        contentClassName="create-edit-post-content"
        overlayMotionProps={{
          initial: { opacity: 0 },
          animate: { opacity: 1 },
          exit: { opacity: 0 },
          transition: { duration: 0.18, ease: 'easeOut' },
        }}
      >
        <div className="relative">
          {!loading && !allMediaReady && (
            <div className="absolute inset-0 bg-white rounded-t-3xl xl:rounded-3xl z-50 flex items-center justify-center">
              <div className="text-center">
                <Loader size="2xl" className="text-purple-600 mx-auto mb-3" />
                <p className="text-gray-600">Laster bilder...</p>
              </div>
            </div>
          )}
          {!loading && compressingImages && (
            <div className="absolute inset-0 talentfunn-modal-overlay backdrop-blur-md rounded-t-3xl xl:rounded-3xl z-50 flex items-center justify-center">
              <div className="bg-white rounded-2xl p-6 shadow-2xl text-center">
                <Loader size="xl" className="text-purple-600 mx-auto mb-3" />
                <p className="text-gray-700 font-medium">{mediaProcessingMessage}</p>
                <p className="text-sm text-gray-500 mt-1">Dette tar bare noen sekunder</p>
              </div>
            </div>
          )}
          {editPostContent}
        </div>
      </FullscreenShell>
      {cropModal}

      <Fragment key="edit-post-optional-modals">
        {/* Preview modal – visuell forhåndsvisning, ingen lagring/publisering */}
        {showPreview && (() => {
          const previewPost = {
            id: 'preview',
            title: title.trim() || 'Untitled',
            description: description.trim() || '',
            coverImageUrl: coverImagePreview || undefined,
            imageUrls: imagePreviews.length > 0 ? imagePreviews : undefined,
            videoId: videoId || undefined,
            user: (userProfile ? { name: userProfile.name ?? 'Deg', location: userProfile.location, county: userProfile.county } : post.user) ?? { name: 'Deg' },
            userId: post.userId ?? userProfile?.id,
            createdAt: post.createdAt || new Date().toISOString(),
          };
          return (
            <PostDetailView
              key="edit-post-preview"
              post={previewPost}
              onClose={() => setShowPreview(false)}
              accessToken={accessToken}
              currentUserId={post.userId ?? userProfile?.id}
              isOwnPost={false}
              isPreview
            />
          );
        })()}

        {/* Video Uploader Modal */}
        {showVideoUploader && (
          <div key="edit-post-video-uploader" className="fixed inset-0 talentfunn-modal-overlay backdrop-blur-md flex items-center justify-center z-[60] p-4 pb-safe">
            <div className="max-w-2xl w-full">
              <VideoUploader
                accessToken={accessToken}
                projectId={post.id}
                onVideoUploaded={handleVideoUploaded}
                onCancel={() => setShowVideoUploader(false)}
              />
            </div>
          </div>
        )}
      </Fragment>

      {/* Publish confirmation – prosjektet blir synlig på profilen */}
      <ConfirmDialog
        key="edit-post-publish-confirm"
        isOpen={showPublishConfirm}
        onClose={() => setShowPublishConfirm(false)}
        onConfirm={() => {
          setShowPublishConfirm(false);
          void handleSubmit(true);
        }}
        title="Publiser prosjekt?"
        message="Når du publiserer prosjektet, blir det synlig på din profil. Er du sikker?"
        confirmText="Publiser"
        cancelText="Avbryt"
        variant="info"
      />

      {/* Delete Video Confirmation Dialog */}
      <ConfirmDialog
        key="edit-post-delete-video-confirm"
        isOpen={showDeleteVideoConfirm}
        onClose={() => setShowDeleteVideoConfirm(false)}
        onConfirm={confirmDeleteVideo}
        title="Slett video?"
        message="Er du sikker på at du vil slette videoen?"
        confirmText="Slett"
        cancelText="Avbryt"
        variant="danger"
        loading={deletingVideo}
        loadingText="Sletter..."
      />

      <ConfirmDialog
        key="edit-post-cancel-confirm"
        isOpen={showCancelConfirm}
        onClose={() => setShowCancelConfirm(false)}
        onConfirm={() => {
          setShowCancelConfirm(false);
          void handleCancel();
        }}
        title="Forkast endringer?"
        message="Du har ulagrede endringer. Er du sikker på at du vil avbryte?"
        confirmText="Ja, avbryt"
        cancelText="Fortsett redigering"
        variant="danger"
      />
    </AnimatePresence>
  );
}